import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Paper,
  Button,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { getPageIcon } from '../../utils/pageNameMapper';
import io from 'socket.io-client';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Format seconds to readable time
 */
const formatTime = (seconds) => {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
};

/**
 * Get status color
 */
const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return '#4CAF50'; // Green
    case 'idle':
      return '#FFC107'; // Yellow
    case 'away':
      return '#F44336'; // Red
    default:
      return '#9E9E9E'; // Gray
  }
};

/**
 * Get status badge
 */
const StatusBadge = ({ status, idleTime }) => {
  const color = getStatusColor(status);
  const text = status === 'active' 
    ? 'Active' 
    : status === 'idle' 
    ? `Idle (${formatTime(idleTime)})` 
    : `Away (${formatTime(idleTime)})`;

  return (
    <Chip
      label={text}
      size="small"
      sx={{
        backgroundColor: `${color}20`,
        color: color,
        border: `1px solid ${color}40`,
        fontWeight: 600,
        fontSize: '0.75rem'
      }}
    />
  );
};

const ActiveUsersOverlay = ({ open, onClose }) => {
  const [, setSessions] = useState([]);
  const [groupedByPage, setGroupedByPage] = useState({});
  const [stats, setStats] = useState({ total: 0, active: 0, idle: 0, away: 0 });
  const [totals, setTotals] = useState({ totalUsers: 0, loggedIn: 0, anonymous: 0 });
  const [statusNow, setStatusNow] = useState({ active: 0, idle: 0, away: 0, loggedInTotal: 0 });
  const [avgCounts, setAvgCounts] = useState(null);
  const [avgTimes, setAvgTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);
  const dataRef = useRef({ sessions: [], groupedByPage: {}, stats: { total: 0, active: 0, idle: 0, away: 0 } });
  const sessionTimeDataRef = useRef(new Map()); // Store base time values and timestamp per session
  const timeUpdateIntervalRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(Date.now()); // Force re-render for time updates
  const [selectedSessions, setSelectedSessions] = useState({});

  // Fetch active users
  const fetchActiveUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/active-users`);
      const data = await response.json();
      
      if (data.success) {
        const newData = {
          sessions: data.sessions || [],
          groupedByPage: data.groupedByPage || {},
          stats: data.stats || { total: 0, active: 0, idle: 0, away: 0 },
          totals: data.totals || null,
          statusNow: data.statusNow || null,
          avgCounts: data.avgCounts || null,
          avgTimes: data.avgTimes || null
        };
        setSessions(newData.sessions);
        setGroupedByPage(newData.groupedByPage);
        setStats(newData.stats);
        if (newData.totals) {
          setTotals(newData.totals);
        }
        if (newData.statusNow) {
          setStatusNow(newData.statusNow);
        }
        if (newData.avgCounts) {
          setAvgCounts(newData.avgCounts);
        }
        if (newData.avgTimes) {
          setAvgTimes(newData.avgTimes);
        }
        dataRef.current = newData;
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching active users:', error);
      setLoading(false);
    }
  };

  const toggleSessionSelected = (sessionId) => {
    setSelectedSessions((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  const getSelectedSessionIds = () =>
    Object.keys(selectedSessions).filter((id) => selectedSessions[id]);

  const handleForceAllBuzzer = () => {
    if (!socketRef.current) return;
    socketRef.current.emit('buzzer:force-open', { mode: 'all' });
  };

  const handleForceSelectedBuzzer = () => {
    if (!socketRef.current) return;
    const ids = getSelectedSessionIds();
    if (!ids.length) return;
    socketRef.current.emit('buzzer:force-open', {
      mode: 'selected',
      sessionIds: ids
    });
  };

  // Setup Socket.IO connection
  useEffect(() => {
    if (!open) return;

    // Initial fetch
    fetchActiveUsers();

    // Setup Socket.IO
    const socket = io(API_BASE, {
      transports: ['websocket'],
      reconnection: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('admin:join-active-users');
    });

    socket.on('active-user:update', (data) => {
      // Always update to get latest time values, even if stats haven't changed
      const updateTimestamp = data.timestamp || Date.now();
      const newSessions = data.sessions || [];
      
      // Store base time values for each session
      const currentSessionIds = new Set(newSessions.map(s => s.session_id));
      newSessions.forEach(session => {
        sessionTimeDataRef.current.set(session.session_id, {
          time_on_current_page: session.time_on_current_page || 0,
          total_session_time: session.total_session_time || 0,
          timestamp: updateTimestamp
        });
      });
      
      // Clean up time data for sessions that no longer exist
      sessionTimeDataRef.current.forEach((_, sessionId) => {
        if (!currentSessionIds.has(sessionId)) {
          sessionTimeDataRef.current.delete(sessionId);
        }
      });
      
      const newData = {
        sessions: newSessions,
        groupedByPage: data.groupedByPage || {},
        stats: data.stats || { total: 0, active: 0, idle: 0, away: 0 },
        totals: data.totals || null,
        statusNow: data.statusNow || null,
        avgCounts: data.avgCounts || null,
        avgTimes: data.avgTimes || null
      };
      setSessions(newData.sessions);
      setGroupedByPage(newData.groupedByPage);
      setStats(newData.stats);
      if (newData.totals) {
        setTotals(newData.totals);
      }
      if (newData.statusNow) {
        setStatusNow(newData.statusNow);
      }
      if (newData.avgCounts) {
        setAvgCounts(newData.avgCounts);
      }
      if (newData.avgTimes) {
        setAvgTimes(newData.avgTimes);
      }
      dataRef.current = newData;
      setLoading(false);
    });

    // Set up real-time time update interval
    timeUpdateIntervalRef.current = setInterval(() => {
      // Update current time to trigger re-render and recalculate displayed times
      setCurrentTime(Date.now());
    }, 1000); // Update every second

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('admin:leave-active-users');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
      // Clear session time data when overlay closes
      sessionTimeDataRef.current.clear();
    };
  }, [open]);

  // Group sessions by page and sort URLs alphabetically
  // URLs are normalized to lowercase for case-insensitive grouping
  const pageGroups = Object.entries(groupedByPage)
    .map(([url, group]) => ({
      url: url.toLowerCase(), // Ensure lowercase for display
      pageName: group.page_name,
      users: group.users || []
    }))
    .sort((a, b) => {
      // Handle root path specially - put it first
      if (a.url === '/') return -1;
      if (b.url === '/') return 1;
      // Sort alphabetically (case-insensitive)
      return a.url.localeCompare(b.url);
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1A1A1A',
          color: '#fff',
          borderRadius: '12px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #333',
          pb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PeopleIcon sx={{ color: '#4CAF50' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Active Users Dashboard
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#fff',
            '&:hover': { backgroundColor: '#333' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Stats Summary - 4 sections */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                mb: 3
              }}
            >
              {/* Section 1: Total Users */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: '#252525',
                  borderRadius: '8px'
                }}
              >
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Total Users
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                      {totals.totalUsers || stats.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total (logged in + guests)
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: '#333' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                      {totals.loggedIn}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Logged in
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#9E9E9E' }}>
                      {totals.anonymous}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Not logged in
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Section 2: Current status (logged-in only) */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: '#252525',
                  borderRadius: '8px'
                }}
              >
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Current Status (Logged in)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                      {statusNow.active}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFC107' }}>
                      {statusNow.idle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Idle
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#F44336' }}>
                      {statusNow.away}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Away
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: '#333' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total logged in:&nbsp;
                      <strong>{statusNow.loggedInTotal}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Section 3: Average counts */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: '#252525',
                  borderRadius: '8px'
                }}
              >
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Average Status Counts (last {avgCounts?.windowMinutes ?? 5} min)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                      {avgCounts ? avgCounts.active.toFixed(1) : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active avg
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFC107' }}>
                      {avgCounts ? avgCounts.idle.toFixed(1) : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Idle avg
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#F44336' }}>
                      {avgCounts ? avgCounts.away.toFixed(1) : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Away avg
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Section 4: Average times */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: '#252525',
                  borderRadius: '8px'
                }}
              >
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Average Time per Logged-in User (last {avgTimes?.windowMinutes ?? 1} min)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                      {avgTimes ? formatTime(Math.round(avgTimes.activeSeconds || 0)) : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active avg time
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFC107' }}>
                      {avgTimes ? formatTime(Math.round(avgTimes.idleSeconds || 0)) : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Idle avg time
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#F44336' }}>
                      {avgTimes ? formatTime(Math.round(avgTimes.awaySeconds || 0)) : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Away avg time
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Buzzer targeting controls */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mb: 2,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={handleForceAllBuzzer}
              >
                Force Buzzer (All)
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                disabled={getSelectedSessionIds().length === 0}
                onClick={handleForceSelectedBuzzer}
              >
                Force Buzzer (Selected)
              </Button>
            </Box>

            {/* Users by Page */}
            {pageGroups.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No active users at the moment
                </Typography>
              </Box>
            ) : (
              <Box>
                {pageGroups.map((group, index) => (
                  <Accordion
                    key={group.url}
                    defaultExpanded={index === 0}
                    sx={{
                      backgroundColor: '#252525',
                      color: '#fff',
                      mb: 1,
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: '0 0 8px 0'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          gap: 2
                        }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        {getPageIcon(group.url)} {group.pageName}
                      </Typography>
                      <Chip
                        label={`${group.users.length} user${group.users.length !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          backgroundColor: '#4CAF5020',
                          color: '#4CAF50',
                          border: '1px solid #4CAF5040',
                          fontWeight: 600
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 'auto', fontSize: '0.85rem' }}
                      >
                        {group.url}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <List sx={{ py: 0 }}>
                        {group.users.map((user, userIndex) => (
                          <React.Fragment key={user.session_id}>
                            {userIndex > 0 && <Divider sx={{ borderColor: '#333', my: 1 }} />}
                            <ListItem
                              sx={{
                                py: 1.5,
                                px: 2,
                                backgroundColor: '#1E1E1E',
                                borderRadius: '8px',
                                mb: 1,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                              }}
                            >
                              <Checkbox
                                edge="start"
                                sx={{ mt: 0.5, color: '#fff' }}
                                checked={!!selectedSessions[user.session_id]}
                                onChange={() => toggleSessionSelected(user.session_id)}
                              />
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      {user.username}
                                    </Typography>
                                    <Chip
                                      label={user.user_type}
                                      size="small"
                                      sx={{
                                        height: '20px',
                                        fontSize: '0.7rem',
                                        backgroundColor: '#333',
                                        color: '#fff'
                                      }}
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <AccessTimeIcon sx={{ fontSize: '1rem', color: '#9E9E9E' }} />
                                      <Typography variant="body2" color="text.secondary">
                                        {formatTime((() => {
                                          // Get stored time data for this session
                                          const timeData = sessionTimeDataRef.current.get(user.session_id);
                                          if (timeData) {
                                            const elapsed = Math.floor((currentTime - timeData.timestamp) / 1000);
                                            // Add elapsed time to base time (assume user is still on page if update was recent)
                                            return timeData.time_on_current_page + (elapsed < 10 ? elapsed : 0);
                                          }
                                          return user.time_on_current_page || 0;
                                        })())} on page
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <AccessTimeIcon sx={{ fontSize: '1rem', color: '#9E9E9E' }} />
                                      <Typography variant="body2" color="text.secondary">
                                        {formatTime((() => {
                                          // Get stored time data for this session
                                          const timeData = sessionTimeDataRef.current.get(user.session_id);
                                          if (timeData) {
                                            const elapsed = Math.floor((currentTime - timeData.timestamp) / 1000);
                                            // Always add elapsed time for total session time
                                            return timeData.total_session_time + elapsed;
                                          }
                                          return user.total_session_time || 0;
                                        })())} total
                                      </Typography>
                                    </Box>
                                    <StatusBadge status={user.status} idleTime={user.idle_time || 0} />
                                  </Box>
                                }
                              />
                            </ListItem>
                          </React.Fragment>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActiveUsersOverlay;

